(() => {
  const reducedMotion = matchMedia('(prefers-reduced-motion: reduce)');
  const animations = new Set();
  const roots = new WeakMap();
  const stopped = () => reducedMotion.matches || document.hidden || document.body.dataset.spacePaused === 'true';
  const cancelMotion = () => {
    if (!stopped()) return;
    animations.forEach(animation => animation.cancel());
    document.querySelectorAll('[data-service-title][data-service-revealed]').forEach(title => { title.dataset.serviceStill = 'true'; });
  };
  const animate = (element, frames, options = {}) => {
    if (!element || stopped() || !element.animate) return;
    const animation = element.animate(frames, { duration: 750, easing: 'cubic-bezier(.16,1,.3,1)', fill: 'backwards', ...options });
    animations.add(animation);
    animation.finished.then(() => animations.delete(animation), () => animations.delete(animation));
  };
  const rise = [{ opacity: .65, translate: '0 12px' }, { opacity: 1, translate: '0 0' }];
  const imageReveal = [{ opacity: .65, scale: '1.015' }, { opacity: 1, scale: '1' }];
  const prepareTitle = title => {
    if (title.dataset.serviceTitle || title.querySelector('a')) return;
    const words = title.textContent.trim().split(/\s+/);
    title.dataset.serviceTitle = 'true';
    title.replaceChildren();
    words.forEach((word,index) => {
      const mask = document.createElement('span');
      const text = document.createElement('span');
      mask.className = 'pp-svc-title-word' + (index === words.length - 1 ? ' pp-svc-title-word--accent' : '');
      mask.style.setProperty('--word-delay', `${Math.min(index,10) * 45}ms`);
      text.textContent = word;
      mask.append(text);
      title.append(mask, ...(index < words.length - 1 ? [' '] : []));
    });
  };
  const initialise = root => {
    if (!root || roots.has(root)) return;
    root.dataset.serviceMotion = 'ready';
    const lifetime = new AbortController();
    root.querySelectorAll('h2,.pp-svc-method-card__title').forEach(prepareTitle);
    root.querySelectorAll('.pp-svc-methods-cluster').forEach((cluster,groupIndex) => {
      const cards = [...cluster.children];
      const names = { 'screen-printing': 'Screen printing', embroidery: 'Embroidery', 'dtg-printing': 'DTG', 'dtf-printing': 'DTF' };
      const tabs = document.createElement('div');
      tabs.className = 'pp-svc-method-tabs';
      tabs.setAttribute('role','tablist');
      tabs.setAttribute('aria-label','Compare printing methods');
      const controls = cards.map((card,index) => {
        const method = card.getAttribute('href')?.split('/').pop() || root.dataset.service;
        const button = document.createElement('button');
        button.type = 'button'; button.textContent = names[method];
        button.setAttribute('role','tab');
        button.id = `${root.dataset.service}-method-${groupIndex}-${index}`;
        const panel = document.createElement('div');
        panel.className = 'pp-svc-method-panel'; panel.id = `${button.id}-panel`;
        panel.setAttribute('role','tabpanel'); panel.setAttribute('aria-labelledby',button.id);
        button.setAttribute('aria-controls',panel.id);
        card.before(panel); panel.append(card); tabs.append(button);
        return { button, panel, card };
      });
      const select = (index, withMotion = true) => {
        controls.forEach(({button,panel},i) => {
          button.setAttribute('aria-selected',String(i===index)); button.tabIndex = i===index ? 0 : -1; panel.hidden = i!==index;
        });
        const card = controls[index].card;
        const title = card.querySelector('[data-service-title]');
        if (title && withMotion) { title.dataset.serviceStill = String(stopped()); title.dataset.serviceRevealed = 'true'; }
        if (withMotion) animate(card.querySelector('.pp-svc-method-photo'), imageReveal, { duration: 650 });
      };
      controls.forEach(({button},index) => {
        button.addEventListener('click', () => select(index), { signal: lifetime.signal });
        button.addEventListener('keydown', event => {
          const next = event.key === 'ArrowRight' ? (index+1)%cards.length : event.key === 'ArrowLeft' ? (index+cards.length-1)%cards.length : event.key === 'Home' ? 0 : event.key === 'End' ? cards.length-1 : null;
          if(next === null) return;
          event.preventDefault(); select(next); controls[next].button.focus();
        }, { signal: lifetime.signal });
      });
      cluster.before(tabs); cluster.dataset.compareReady = 'true';
      select(Math.max(0,cards.findIndex(card => card.classList.contains('pp-svc-method-card--active'))),false);
    });
    const observer = 'IntersectionObserver' in window ? new IntersectionObserver(entries => {
      entries.forEach(entry => {
        if (!entry.isIntersecting) return;
        const element = entry.target;
        observer.unobserve(element);
        element.dataset.serviceStill = String(stopped());
        element.dataset.serviceRevealed = 'true';
        const delay = Number(element.dataset.serviceDelay || 0);
        if (element.matches('[data-service-title]')) {
          // CSS handles the word masks and the accent fill.
        } else if (element.matches('.pp-svc-method-card')) {
          animate(element.querySelector('.pp-svc-method-photo'), imageReveal, { duration: 950, delay });
          element.querySelectorAll('p,[class$="__use"],[class$="__arrow"]').forEach((part,index) => animate(part, rise, { delay: delay + 80 + index * 55 }));
        } else if (element.matches('.pp-svc-quality__img-wrap,.pp-svc-photo,.pp-svc-moq__img-wrap,.pp-svc-trade__img-wrap')) {
          animate(element, imageReveal, { duration: 1000, delay });
        } else {
          animate(element, rise, { delay });
        }
      });
    }, { threshold: 0, rootMargin: '0px 0px 80px 0px' }) : null;

    const selectors = '[data-service-title],.pp-svc-quality__img-wrap,.pp-svc-photo:not(.pp-svc-method-photo),.pp-svc-product-card,.pp-svc-method-card,.pp-svc-step,.pp-svc-benefit,.pp-svc-industry,.pp-svc-moq__img-wrap,.pp-svc-trade__img-wrap,.dtg-compare-row,.dtf-compare-row,.pp-svc-final-cta__inner,.pp-photo-wall__tile';
    root.querySelectorAll(selectors).forEach(element => {
      const siblings = [...element.parentElement.children].filter(child => child.matches(selectors));
      element.dataset.serviceDelay = String(Math.min(siblings.indexOf(element) % 4, 3) * 75);
      observer?.observe(element);
    });
    root.querySelectorAll('details.pp-svc-industry,details.pp-svc-readmore').forEach(details => details.addEventListener('toggle', () => {
      if (!details.open) return;
      if (details.classList.contains('pp-svc-industry')) [...details.parentElement.children].forEach(sibling => { if (sibling !== details) sibling.open = false; });
      animate(details.querySelector('.pp-svc-disclosure-copy'), rise, { duration: 350 });
    }, { signal: lifetime.signal }));

    // The HTML is always visible; motion never gates reading, links or keyboard access.
    root.addEventListener('focusin', event => {
      animations.forEach(animation => {
        if (animation.effect?.target?.contains(event.target)) animation.cancel();
      });
    }, { signal: lifetime.signal });
    root.addEventListener('click', event => {
      const question = event.target.closest('.pp-svc-faq__q');
      if (question?.getAttribute('aria-expanded') === 'true') animate(question.nextElementSibling, rise, { duration: 350 });
    }, { signal: lifetime.signal });

    animate(root.querySelector('.pp-svc-hero__media img'), [{ scale: '1.055' }, { scale: '1' }], { duration: 1500 });
    root.querySelectorAll('.pp-svc-hero__inner > *').forEach((part,index) => animate(part, rise, { delay: index * 85, duration: 850 }));
    roots.set(root, () => {
      observer?.disconnect();
      lifetime.abort();
      animations.forEach(animation => { if (root.contains(animation.effect?.target)) animation.cancel(); });
      roots.delete(root);
    });
  };
  reducedMotion.addEventListener('change', cancelMotion);
  document.addEventListener('visibilitychange', cancelMotion);
  new MutationObserver(cancelMotion).observe(document.body, { attributes: true, attributeFilter: ['data-space-paused'] });
  document.querySelectorAll('.pp-service-page').forEach(initialise);
  document.addEventListener('shopify:section:load', event => event.target.querySelectorAll('.pp-service-page').forEach(initialise));
  document.addEventListener('shopify:section:unload', event => event.target.querySelectorAll('.pp-service-page').forEach(root => roots.get(root)?.()));
})();
